"use client";

import { c as _c } from "react/compiler-runtime";
import { CalendarIcon, MapPin, Search, X } from "lucide-react";
import { useRouter } from "next/navigation";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { MobileDateTimeModal } from "~/app/(site)/_components/MobileDateTimeModal";
import { OutOfAreaNotice } from "~/app/(site)/_components/OutOfAreaNotice";
import { DateTimeFields } from "~/app/(site)/search/_components/DateTimeFields";
import { type VehicleSize, VehicleSizePicker } from "~/app/(site)/search/_components/VehicleSizePicker";
import { Button } from "~/components/ui/button";
import { useDestinationTimeZone } from "~/hooks/useDestinationTimeZone";
import { useSearchWindow } from "~/hooks/useSearchWindow";
import { safeUUID } from "~/lib/auth/uuid";
import { type PlaceSuggestion, type RetrievedPlace, retrievePlace, suggestPlaces } from "~/lib/mapbox/mapbox";
import { buildSearchUrl } from "~/lib/search-url";
import { isWithinServiceArea } from "~/lib/service-area";
import { formatShort } from "~/lib/utils/datetime";
import type { VehicleRow } from "~/server/api/routers/vehicles";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type { VehicleSize };
export type SearchBarValues = {
  query: string;
  starts_at: string;
  ends_at: string;
  lat?: number;
  lng?: number;
  city?: string;
  state?: string;
  vehicleSize: VehicleSize | null;
  vehicle?: VehicleRow | null;
};

// ---------------------------------------------------------------------------
// Shared field-row styles
// ---------------------------------------------------------------------------

const _FIELD_BASE = ["flex items-center gap-3 rounded-[var(--r-lg)] border px-4 py-3.5 text-left", "[transition:border-color_var(--dur-fast)_var(--ease-snap),background-color_var(--dur-fast)_var(--ease-snap),box-shadow_var(--dur-fast)_var(--ease-snap)]"].join(" ");
const _FIELD_DEFAULT = "border-[var(--ink-20)] bg-[var(--ink-05)]";
const _FIELD_ACTIVE = "border-[var(--blue)] bg-white shadow-[0_0_0_3px_rgba(0,115,233,0.12)]";
const _FIELD_ERROR = "border-[var(--red)] bg-white shadow-[0_0_0_3px_rgba(255,59,48,0.10)]";
const _FIELD_HOVER = "hover:border-[var(--blue)]";

// ---------------------------------------------------------------------------
// SearchCard
// ---------------------------------------------------------------------------

interface SearchCardProps {
  initialQuery?: string;
  initialStartsAt?: string;
  initialEndsAt?: string;
  initialVehicleSize?: VehicleSize;
  defaultVehicle?: VehicleRow | null;
  initialLat?: number;
  initialLng?: number;
  initialCity?: string;
  initialState?: string;
  onValuesChange?: (values: SearchBarValues) => void;
  onLocationSelect?: (coords: {
    lat: number;
    lng: number;
  }) => void;
}
export function SearchCard(t0) {
  const $ = _c(148);
  const {
    initialQuery: t1,
    initialStartsAt,
    initialEndsAt,
    initialVehicleSize,
    defaultVehicle,
    initialLat,
    initialLng,
    initialCity: t2,
    initialState: t3,
    onLocationSelect
  } = t0;
  const initialQuery = t1 === undefined ? "" : t1;
  const initialCity = t2 === undefined ? "" : t2;
  const initialState = t3 === undefined ? "" : t3;
  const router = useRouter();
  const [location, setLocation] = useState(initialQuery);
  let t4;
  if ($[0] !== initialCity || $[1] !== initialLat || $[2] !== initialLng || $[3] !== initialQuery || $[4] !== initialState) {
    t4 = initialLat !== undefined && initialLng !== undefined ? {
      label: initialQuery,
      name: null,
      lat: initialLat,
      lng: initialLng,
      city: initialCity,
      state: initialState
    } : null;
    $[0] = initialCity;
    $[1] = initialLat;
    $[2] = initialLng;
    $[3] = initialQuery;
    $[4] = initialState;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [selectedPlace, setSelectedPlace] = useState(t4);
  const destTimeZone = useDestinationTimeZone(selectedPlace?.lat, selectedPlace?.lng);
  let t5;
  if ($[6] !== initialStartsAt) {
    t5 = initialStartsAt ? {
      starts_at: initialStartsAt
    } : {};
    $[6] = initialStartsAt;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== initialEndsAt) {
    t6 = initialEndsAt ? {
      ends_at: initialEndsAt
    } : {};
    $[8] = initialEndsAt;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = {
      ...t5,
      ...t6
    };
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    starts_at: startsAt,
    ends_at: endsAt,
    now,
    setStartsAt,
    setEndsAt
  } = useSearchWindow(t7, destTimeZone);
  const [vehicleSize, setVehicleSize] = useState(initialVehicleSize ?? defaultVehicle?.size ?? null);
  const [, setSelectedVehicle] = useState(defaultVehicle ?? null);
  const [appliedDefaultId, setAppliedDefaultId] = useState(defaultVehicle?.id ?? null);
  if (defaultVehicle?.id !== appliedDefaultId && vehicleSize === null && defaultVehicle) {
    setAppliedDefaultId(defaultVehicle.id);
    setVehicleSize(defaultVehicle.size as VehicleSize);
    setSelectedVehicle(defaultVehicle);
  }
  const locationRef = useRef(null);
  const locationInputRef = useRef(null);
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const [suggestions, setSuggestions] = useState(t8);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [sessionToken] = useState(_temp);
  const debounceRef = useRef(null);
  const [dateTimeOpen, setDateTimeOpen] = useState(false);
  const dateTimeTriggerRef = useRef(null);
  let t10;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const handler = e => {
        if (!locationRef.current?.contains(e.target as Node)) {
          setShowSuggestions(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    };
    t10 = [];
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[16] !== dateTimeOpen) {
    t11 = () => {
      if (!dateTimeOpen) {
        return;
      }
      const handler_0 = e_0 => {
        if (!dateTimeTriggerRef.current?.contains(e_0.target as Node) && !(e_0.target as Element).closest?.("[data-ezspot-calendar]") && !(e_0.target as Element).closest?.("[data-mobile-datetime-modal]")) {
          setDateTimeOpen(false);
        }
      };
      document.addEventListener("mousedown", handler_0);
      return () => document.removeEventListener("mousedown", handler_0);
    };
    t12 = [dateTimeOpen];
    $[16] = dateTimeOpen;
    $[17] = t11;
    $[18] = t12;
  } else {
    t11 = $[17];
    t12 = $[18];
  }
  useEffect(t11, t12);
  let t13;
  if ($[19] !== sessionToken) {
    t13 = value => {
      setLocation(value);
      setSelectedPlace(null);
      setActiveSuggestionIndex(-1);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (value.trim().length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        setIsLoading(true);
        const results = await suggestPlaces(value, sessionToken);
        setIsLoading(false);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      }, 300);
    };
    $[19] = sessionToken;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const handleLocationChange = t13;
  let t14;
  if ($[21] !== onLocationSelect || $[22] !== sessionToken) {
    t14 = async suggestion => {
      setShowSuggestions(false);
      setSuggestions([]);
      setIsLoading(true);
      const place = await retrievePlace(suggestion.mapbox_id, sessionToken);
      setIsLoading(false);
      if (place) {
        setLocation(place.label || suggestion.name);
        setSelectedPlace(place);
        onLocationSelect?.({
          lat: place.lat,
          lng: place.lng
        });
      } else {
        setLocation(suggestion.name);
      }
    };
    $[21] = onLocationSelect;
    $[22] = sessionToken;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const handleSelect = t14;
  let canSearch;
  let clearLocation;
  let handleKeyDown;
  let handleSearch;
  let outOfArea;
  if ($[24] !== activeSuggestionIndex || $[25] !== endsAt || $[26] !== handleSelect || $[27] !== location || $[28] !== router || $[29] !== selectedPlace || $[30] !== showSuggestions || $[31] !== startsAt || $[32] !== suggestions || $[33] !== vehicleSize) {
    handleKeyDown = e_1 => {
      if (!showSuggestions || suggestions.length === 0) {
        if (e_1.key === "Enter") {
          handleSearch();
        }
        return;
      }
      if (e_1.key === "ArrowDown") {
        e_1.preventDefault();
        setActiveSuggestionIndex(i => Math.min(i + 1, suggestions.length - 1));
      } else {
        if (e_1.key === "ArrowUp") {
          e_1.preventDefault();
          setActiveSuggestionIndex(_temp2);
        } else {
          if (e_1.key === "Enter") {
            e_1.preventDefault();
            const active = suggestions[activeSuggestionIndex];
            if (active) {
              handleSelect(active);
            } else {
              handleSearch();
            }
          } else {
            if (e_1.key === "Escape") {
              setShowSuggestions(false);
              setActiveSuggestionIndex(-1);
            }
          }
        }
      }
    };
    let t15;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = () => {
        setLocation("");
        setSelectedPlace(null);
        setSuggestions([]);
        setShowSuggestions(false);
      };
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    clearLocation = t15;
    let t16;
    if ($[40] !== selectedPlace) {
      t16 = selectedPlace != null && !isWithinServiceArea(selectedPlace.lat, selectedPlace.lng);
      $[40] = selectedPlace;
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    outOfArea = t16;
    let t17;
    if ($[42] !== endsAt || $[43] !== location || $[44] !== outOfArea || $[45] !== selectedPlace || $[46] !== startsAt || $[47] !== vehicleSize) {
      t17 = location.trim().length > 0 && !!selectedPlace && !outOfArea && !!startsAt && !!endsAt && vehicleSize !== null;
      $[42] = endsAt;
      $[43] = location;
      $[44] = outOfArea;
      $[45] = selectedPlace;
      $[46] = startsAt;
      $[47] = vehicleSize;
      $[48] = t17;
    } else {
      t17 = $[48];
    }
    canSearch = t17;
    handleSearch = () => {
      if (outOfArea) {
        return;
      }
      const url = buildSearchUrl({
        q: location,
        place: selectedPlace,
        starts_at: startsAt,
        ends_at: endsAt,
        vehicleSize
      });
      if (url) {
        router.push(url);
      }
    };
    $[24] = activeSuggestionIndex;
    $[25] = endsAt;
    $[26] = handleSelect;
    $[27] = location;
    $[28] = router;
    $[29] = selectedPlace;
    $[30] = showSuggestions;
    $[31] = startsAt;
    $[32] = suggestions;
    $[33] = vehicleSize;
    $[34] = canSearch;
    $[35] = clearLocation;
    $[36] = handleKeyDown;
    $[37] = handleSearch;
    $[38] = outOfArea;
  } else {
    canSearch = $[34];
    clearLocation = $[35];
    handleKeyDown = $[36];
    handleSearch = $[37];
    outOfArea = $[38];
  }
  const t15 = showSuggestions ? "border-[var(--blue)] bg-white shadow-[0_0_0_3px_rgba(0,115,233,0.12)]" : "border-[var(--ink-20)] bg-[var(--ink-05)] hover:border-[var(--blue)]";
  let t16;
  if ($[49] !== t15) {
    t16 = ["flex items-center gap-3 rounded-[var(--r-lg)] border px-4 py-3.5 transition-colors focus-within:bg-white", t15, "[transition:border-color_var(--dur-fast)_var(--ease-snap),background-color_var(--dur-fast)_var(--ease-snap),box-shadow_var(--dur-fast)_var(--ease-snap)]"];
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  const t17 = t16.join(" ");
  let t18;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <MapPin className="h-4 w-4 flex-shrink-0 text-secondary" />;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="text-[10px] font-bold uppercase tracking-wide text-secondary">Location</span>;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== handleLocationChange) {
    t20 = e_2 => handleLocationChange(e_2.target.value);
    $[53] = handleLocationChange;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== suggestions.length) {
    t21 = () => {
      if (suggestions.length > 0) {
        setShowSuggestions(true);
      }
    };
    $[55] = suggestions.length;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  const t22 = activeSuggestionIndex >= 0 ? `location-suggestion-${suggestions[activeSuggestionIndex]?.mapbox_id}` : undefined;
  let t23;
  if ($[57] !== handleKeyDown || $[58] !== location || $[59] !== showSuggestions || $[60] !== t20 || $[61] !== t21 || $[62] !== t22) {
    t23 = <div className="flex flex-1 flex-col min-w-0 text-left">{t19}<input ref={locationInputRef} type="text" role="combobox" value={location} onChange={t20} onKeyDown={handleKeyDown} onFocus={t21} placeholder="Where do you need parking?" className="w-full border-none bg-transparent text-[14px] font-medium text-body outline-none placeholder:font-normal placeholder:text-muted" autoComplete="off" aria-autocomplete="list" aria-expanded={showSuggestions} aria-controls="location-suggestions" aria-activedescendant={t22} /></div>;
    $[57] = handleKeyDown;
    $[58] = location;
    $[59] = showSuggestions;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== clearLocation || $[65] !== isLoading || $[66] !== location.length) {
    t24 = isLoading ? <span className="inline-block h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-border border-t-accent" /> : location.length > 0 ? <button type="button" aria-label="Clear location" onMouseDown={_temp3} onClick={clearLocation} className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[var(--ink-20)] transition-colors hover:bg-[var(--ink-40)]"><X className="h-2 w-2 text-black" strokeWidth={3} /></button> : null;
    $[64] = clearLocation;
    $[65] = isLoading;
    $[66] = location.length;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== t17 || $[69] !== t23 || $[70] !== t24) {
    t25 = <div className={t17}>{t18}{t23}{t24}</div>;
    $[68] = t17;
    $[69] = t23;
    $[70] = t24;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] !== activeSuggestionIndex || $[73] !== handleSelect || $[74] !== showSuggestions || $[75] !== suggestions) {
    t26 = showSuggestions && suggestions.length > 0 && <ul id="location-suggestions" className="absolute left-0 top-full z-[100] mt-1 max-h-72 w-full overflow-y-auto overflow-x-hidden rounded-xl border border-border bg-white shadow-lg">{suggestions.map((suggestion_0, index) => <li key={suggestion_0.mapbox_id} role="presentation"><button id={`location-suggestion-${suggestion_0.mapbox_id}`} type="button" role="option" aria-selected={index === activeSuggestionIndex} className={`flex w-full items-start gap-2.5 px-3.5 py-2.5 text-left text-[13px] transition-colors ${index === activeSuggestionIndex ? "bg-[#EAF6FF]" : "hover:bg-[#F5FBFF]"}`} onMouseDown={e_4 => {
          e_4.preventDefault();
          handleSelect(suggestion_0);
        }} onMouseEnter={() => setActiveSuggestionIndex(index)}><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" /><span className="min-w-0"><span className="block truncate font-medium text-body">{suggestion_0.name}</span>{suggestion_0.place_formatted && <span className="block truncate text-[12px] text-secondary">{suggestion_0.place_formatted}</span>}</span></button></li>)}</ul>;
    $[72] = activeSuggestionIndex;
    $[73] = handleSelect;
    $[74] = showSuggestions;
    $[75] = suggestions;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  let t27;
  if ($[77] !== clearLocation || $[78] !== outOfArea || $[79] !== selectedPlace?.city) {
    t27 = outOfArea && <OutOfAreaNotice city={selectedPlace?.city} onChangeDestination={() => {
      clearLocation();
      locationInputRef.current?.focus();
    }} />;
    $[77] = clearLocation;
    $[78] = outOfArea;
    $[79] = selectedPlace?.city;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  let t28;
  if ($[81] !== t25 || $[82] !== t26 || $[83] !== t27) {
    t28 = <div className="relative" ref={locationRef}>{t25}{t26}{t27}</div>;
    $[81] = t25;
    $[82] = t26;
    $[83] = t27;
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  const locationField = t28;
  let t29;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      background: "var(--color-surface)",
      border: "1px solid var(--color-border)"
    };
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== locationField) {
    t30 = <div>{locationField}</div>;
    $[86] = locationField;
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  let t31;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setDateTimeOpen(_temp4);
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  const t32 = dateTimeOpen ? _FIELD_ACTIVE : _FIELD_DEFAULT;
  let t33;
  if ($[89] !== t32) {
    t33 = [_FIELD_BASE, "w-full", t32, _FIELD_HOVER];
    $[89] = t32;
    $[90] = t33;
  } else {
    t33 = $[90];
  }
  const t34 = t33.join(" ");
  let t35;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <CalendarIcon className="h-4 w-4 shrink-0 text-secondary" />;
    $[91] = t35;
  } else {
    t35 = $[91];
  }
  let t36;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <span className="text-[10px] font-bold uppercase tracking-wide text-secondary">Start</span>;
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  let t37;
  if ($[93] !== startsAt) {
    t37 = formatShort(startsAt);
    $[93] = startsAt;
    $[94] = t37;
  } else {
    t37 = $[94];
  }
  let t38;
  if ($[95] !== t37) {
    t38 = <div className="flex min-w-0 flex-1 flex-col items-start">{t36}<span className="w-full truncate text-[14px] font-medium text-body">{t37}</span></div>;
    $[95] = t37;
    $[96] = t38;
  } else {
    t38 = $[96];
  }
  let t39;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className="w-px h-5 shrink-0 bg-[var(--ink-10)]" />;
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  let t40;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <span className="text-[10px] font-bold uppercase tracking-wide text-secondary">End</span>;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== endsAt) {
    t41 = formatShort(endsAt);
    $[99] = endsAt;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  let t42;
  if ($[101] !== t41) {
    t42 = <div className="flex min-w-0 flex-1 flex-col items-start">{t40}<span className="w-full truncate text-[14px] font-medium text-body">{t41}</span></div>;
    $[101] = t41;
    $[102] = t42;
  } else {
    t42 = $[102];
  }
  let t43;
  if ($[103] !== t38 || $[104] !== t42) {
    t43 = <div className="flex flex-1 items-center gap-4 min-w-0">{t38}{t39}{t42}</div>;
    $[103] = t38;
    $[104] = t42;
    $[105] = t43;
  } else {
    t43 = $[105];
  }
  let t44;
  if ($[106] !== t34 || $[107] !== t43) {
    t44 = <button type="button" onClick={t31} className={t34}>{t35}{t43}</button>;
    $[106] = t34;
    $[107] = t43;
    $[108] = t44;
  } else {
    t44 = $[108];
  }
  let t45;
  if ($[109] !== dateTimeOpen || $[110] !== destTimeZone || $[111] !== endsAt || $[112] !== now || $[113] !== setEndsAt || $[114] !== setStartsAt || $[115] !== startsAt) {
    t45 = dateTimeOpen && <div className="hidden md:block absolute left-0 top-full mt-2 z-50 w-full min-w-[340px] rounded-2xl border border-[var(--ink-20)] bg-white p-4 shadow-2xl"><DateTimeFields startsAt={startsAt} endsAt={endsAt} onStartsAtChange={setStartsAt} onEndsAtChange={setEndsAt} now={now} timeZone={destTimeZone} /></div>;
    $[109] = dateTimeOpen;
    $[110] = destTimeZone;
    $[111] = endsAt;
    $[112] = now;
    $[113] = setEndsAt;
    $[114] = setStartsAt;
    $[115] = startsAt;
    $[116] = t45;
  } else {
    t45 = $[116];
  }
  let t46;
  if ($[117] !== t44 || $[118] !== t45) {
    t46 = <div className="relative" ref={dateTimeTriggerRef}>{t44}{t45}</div>;
    $[117] = t44;
    $[118] = t45;
    $[119] = t46;
  } else {
    t46 = $[119];
  }
  let t47;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = () => setDateTimeOpen(false);
    $[120] = t47;
  } else {
    t47 = $[120];
  }
  let t48;
  if ($[121] !== dateTimeOpen || $[122] !== destTimeZone || $[123] !== endsAt || $[124] !== now || $[125] !== setEndsAt || $[126] !== setStartsAt || $[127] !== startsAt) {
    t48 = <MobileDateTimeModal open={dateTimeOpen} onClose={t47} startsAt={startsAt} endsAt={endsAt} onStartsAtChange={setStartsAt} onEndsAtChange={setEndsAt} now={now} timeZone={destTimeZone} />;
    $[121] = dateTimeOpen;
    $[122] = destTimeZone;
    $[123] = endsAt;
    $[124] = now;
    $[125] = setEndsAt;
    $[126] = setStartsAt;
    $[127] = startsAt;
    $[128] = t48;
  } else {
    t48 = $[128];
  }
  let t49;
  if ($[129] !== t46 || $[130] !== t48) {
    t49 = <div>{t46}{t48}</div>;
    $[129] = t46;
    $[130] = t48;
    $[131] = t49;
  } else {
    t49 = $[131];
  }
  let t50;
  if ($[132] !== t30 || $[133] !== t49) {
    t50 = <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">{t30}{t49}</div>;
    $[132] = t30;
    $[133] = t49;
    $[134] = t50;
  } else {
    t50 = $[134];
  }
  let t51;
  if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = size => setVehicleSize(size);
    $[135] = t51;
  } else {
    t51 = $[135];
  }
  let t52;
  if ($[136] !== vehicleSize) {
    t52 = <div className="mb-4"><VehicleSizePicker value={vehicleSize} onChange={t51} /></div>;
    $[136] = vehicleSize;
    $[137] = t52;
  } else {
    t52 = $[137];
  }
  let t53;
  if ($[138] !== handleSearch) {
    t53 = () => handleSearch();
    $[138] = handleSearch;
    $[139] = t53;
  } else {
    t53 = $[139];
  }
  const t54 = !canSearch;
  let t55;
  if ($[140] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <Search className="mr-2 h-4 w-4 shrink-0" strokeWidth={2.5} />;
    $[140] = t55;
  } else {
    t55 = $[140];
  }
  let t56;
  if ($[141] !== t53 || $[142] !== t54) {
    t56 = <Button type="button" onClick={t53} variant="primary" size="md" disabled={t54} className="min-h-[48px] w-full text-[14px]">{t55}Find Parking Spots</Button>;
    $[141] = t53;
    $[142] = t54;
    $[143] = t56;
  } else {
    t56 = $[143];
  }
  let t57;
  if ($[144] !== t50 || $[145] !== t52 || $[146] !== t56) {
    t57 = <div className="relative z-10 w-full max-w-[820px] rounded-2xl p-4 sm:p-5" style={t29}>{t50}{t52}{t56}</div>;
    $[144] = t50;
    $[145] = t52;
    $[146] = t56;
    $[147] = t57;
  } else {
    t57 = $[147];
  }
  return t57;
}
function _temp4(o) {
  return !o;
}
function _temp3(e_3) {
  return e_3.preventDefault();
}
function _temp2(i_0) {
  return Math.max(i_0 - 1, -1);
}
function _temp() {
  return safeUUID();
}
export function SearchCardSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      background: "var(--color-surface)",
      border: "1px solid var(--color-border)"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="w-full max-w-[820px] rounded-2xl p-4 sm:p-5" style={t0}><div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2"><div className="h-[62px] w-full animate-pulse rounded-[var(--r-lg)] bg-[var(--ink-10)]" /><div className="h-[62px] w-full animate-pulse rounded-[var(--r-lg)] bg-[var(--ink-10)]" /></div><div className="mb-4 h-[62px] w-full animate-pulse rounded-[var(--r-lg)] bg-[var(--ink-10)]" /><div className="h-[48px] w-full animate-pulse rounded-[var(--r-full)] bg-[var(--ink-10)]" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}